import { doc, setDoc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from './firebase';

/**
 * Maintenance Service
 * Handles maintenance mode status in Firebase
 */

const MAINTENANCE_DOC_ID = 'maintenance_status';

/**
 * Sets the maintenance mode status
 * @param {boolean} isMaintenanceMode - Whether maintenance mode is enabled
 * @param {string} adminUser - Admin user making the change
 * @returns {Promise<void>}
 */
export const setMaintenanceMode = async (isMaintenanceMode, adminUser = 'admin') => {
  try {
    const maintenanceDoc = doc(db, 'system_settings', MAINTENANCE_DOC_ID);
    
    await setDoc(maintenanceDoc, {
      isMaintenanceMode,
      lastUpdatedAt: new Date(),
      updatedBy: adminUser,
      message: 'El sitio se encuentra en mantenimiento. Por favor, intenta más tarde.'
    }, { merge: true });
    
    console.log(`Maintenance mode ${isMaintenanceMode ? 'enabled' : 'disabled'} by ${adminUser}`);
  } catch (error) {
    console.error('Error setting maintenance mode:', error);
    throw new Error('Error al actualizar el modo de mantenimiento');
  }
};

/**
 * Gets the current maintenance mode status
 * @returns {Promise<Object>} Maintenance status object
 */
export const getMaintenanceStatus = async () => {
  try {
    const maintenanceDoc = doc(db, 'system_settings', MAINTENANCE_DOC_ID);
    const docSnap = await getDoc(maintenanceDoc);
    
    if (docSnap.exists()) {
      return docSnap.data();
    } else {
      // Return default status if document doesn't exist
      return {
        isMaintenanceMode: false,
        lastUpdatedAt: null,
        updatedBy: null,
        message: 'El sitio se encuentra en mantenimiento. Por favor, intenta más tarde.'
      };
    }
  } catch (error) {
    console.error('Error getting maintenance status:', error);
    // Return safe default on error
    return {
      isMaintenanceMode: false,
      lastUpdatedAt: null,
      updatedBy: null,
      message: 'El sitio se encuentra en mantenimiento. Por favor, intenta más tarde.'
    };
  }
};

/**
 * Subscribes to maintenance status changes
 * @param {Function} callback - Callback function to handle status changes
 * @returns {Function} Unsubscribe function
 */
export const subscribeToMaintenanceStatus = (callback) => {
  try {
    const maintenanceDoc = doc(db, 'system_settings', MAINTENANCE_DOC_ID);
    
    return onSnapshot(maintenanceDoc, (docSnap) => {
      if (docSnap.exists()) {
        callback(docSnap.data());
      } else {
        // Document doesn't exist, return default status
        callback({
          isMaintenanceMode: false,
          lastUpdatedAt: null,
          updatedBy: null,
          message: 'El sitio se encuentra en mantenimiento. Por favor, intenta más tarde.'
        });
      }
    }, (error) => {
      console.error('Error listening to maintenance status:', error);
      // Return safe default on error
      callback({
        isMaintenanceMode: false,
        lastUpdatedAt: null,
        updatedBy: null,
        message: 'El sitio se encuentra en mantenimiento. Por favor, intenta más tarde.'
      });
    });
  } catch (error) {
    console.error('Error subscribing to maintenance status:', error);
    // Return a no-op unsubscribe function
    return () => {};
  }
};